<template>
  <Loading v-if="isLoading" />
  <div v-else>
    <PopupAddFloorplan ref="addFloorplanPopup" :venueId="activeVenueId" @onAddedNew="refetchFloorplans" />
    <HandleTableSidebar ref="sidebar" @onClickRemoveTable="onClickRemoveTable" />

    <div class="flex justify-between">
      <div class="flex">
        <vs-button icon="add" @click="$refs.addFloorplanPopup.show()" color="primary" class="mr-3"> New floorplan </vs-button>
        <vs-button icon="save" @click="onClickSave" color="primary" class="mr-3"> Save floorplans </vs-button>
        <vs-select width="180px" v-model="activeFloorplan">
          <vs-select-item :key="floorplan.id" :value="floorplan" :text="floorplan.name" v-for="floorplan in floorplans" />
        </vs-select>
      </div>
      <vs-button icon="delete" @click="confirmDelete" color="danger" class="mr-3"> Delete floorplan </vs-button>
    </div>
    <vs-divider> Floorplan: {{ activeFloorplan.name }} </vs-divider>
    <div class="flex justify-between">
      <div>
        <SidebarItem label="Floorplan name">
          <vs-input v-model="activeFloorplan.name" />
        </SidebarItem>
      </div>
      <div>
        <div class="flex gap-4">
          <div @drag="drag" @dragend="dragend(false, false, false)" class="droppable-element" draggable="true" unselectable="on">
            <div
              style="width: 74px; height: 74px"
              class="bg-white flex place-items-center text-center border border-solid border-black rounded-lg cursor-move"
            >
              <strong class="text-xl">New table</strong>
            </div>
          </div>
          <div @drag="drag" @dragend="dragend(false, false, true)" class="droppable-element" draggable="true" unselectable="on">
            <div
              style="width: 74px; height: 74px"
              class="bg-white flex place-items-center text-center border border-solid border-black rounded-lg cursor-move transform rotate-45 mx-2"
            >
              <strong class="text-xl transform -rotate-45">New table</strong>
            </div>
          </div>
          <div @drag="drag" @dragend="dragend(false, true, false)" class="droppable-element" draggable="true" unselectable="on">
            <div
              style="width: 74px; height: 74px"
              class="bg-white flex place-items-center text-center border border-solid border-black rounded-lg cursor-move rounded-full"
            >
              <strong class="text-xl">New table</strong>
            </div>
          </div>

          <div @drag="drag" @dragend="dragend(true, false, false)" class="droppable-element" draggable="true" unselectable="on">
            <div
              style="width: 74px; height: 74px; background-color: #444"
              class="text-white flex place-items-center text-center border border-solid border-black rounded-lg striped-bg cursor-move"
            >
              <strong class="text-xl">New block</strong>
            </div>
          </div>

          <div @drag="drag" @dragend="dragend(true, false, true)" class="droppable-element" draggable="true" unselectable="on">
            <div
              style="width: 74px; height: 74px; background-color: #444"
              class="text-white flex place-items-center text-center border border-solid border-black rounded-lg striped-bg cursor-move transform rotate-45 mx-2"
            >
              <strong class="text-xl transform -rotate-45">New block</strong>
            </div>
          </div>

          <div @drag="drag" @dragend="dragend(true, true, false)" class="droppable-element" draggable="true" unselectable="on">
            <div
              style="width: 74px; height: 74px; background-color: #444"
              class="text-white flex place-items-center text-center border border-solid border-black rounded-lg striped-bg cursor-move rounded-full"
            >
              <strong class="text-xl">New block</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-8" v-if="activeFloorplan">
      <div id="floorplanWrapper">
        <grid-layout
          ref="gridlayout"
          class="tables-container-wrapper"
          :layout.sync="activeFloorplan.tables"
          :col-num="colNum"
          :max-rows="42"
          :row-height="9"
          :margin="[4, 4]"
          :is-resizable="true"
          :is-draggable="true"
          :vertical-compact="false"
          :preventCollision="true"
          :use-css-transforms="false"
          :restoreOnDrag="false"
          :is-bounded="false"
          :responsive="false"
        >
          <template #default="{ gridItemProps }">
            <grid-item
              v-for="item in activeFloorplan.tables"
              :key="item.i"
              :minW="item.isNonTable ? 1 : 4"
              :minH="item.isNonTable ? 1 : 4"
              :i="item.i"
              :x="item.x"
              :y="item.y"
              :w="item.w"
              :h="item.h"
              v-bind="gridItemProps"
              @move="move = true"
              @moved="onMoved"
              :class="[
                {
                  'static-element': item.isNonTable,
                  'round-table': item.isRound,
                  'transform rotate-45': item.isRotated,
                  'striped-bg': item.isNonTable
                }
              ]"
              style="touch-action: none"
            >
              <div v-if="item.isNontable" class="h-full">
                <div class="flex justify-center items-center h-full">
                  <strong class="opacity-50" :class="{ 'transform -rotate-45': item.isRotated }">{{ item.name }}</strong>
                </div>
              </div>
              <div v-else class="h-full flex justify-center items-center flex-col gap-1" @click="onClickTable(item)">
                <div class="text-center" :class="{ 'transform -rotate-45': item.isRotated }">
                  <strong class="text-xl">{{ item.name }}</strong>
                </div>

                <!-- <div
                  v-if="!isAvailable(item) && !item.isNontable"
                  class="absolute top-2 left-2"
                >
                  <div>
                    <i class="pi pi-shopping-bag"> </i>
                    {{ item.orderLines }}
                  </div>
                </div>
                <div
                  v-if="!isAvailable(item) && !item.isNontable"
                  class="absolute top-2 right-2"
                >
                  <div>{{ item.numGuests }} <i class="pi pi-user"> </i></div>
                </div> -->
              </div>
            </grid-item>
          </template>
        </grid-layout>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import HandleTableSidebar from "./VenueTableSidebar.vue";
import VueGridLayout from "vue-grid-layout";
import { ClientSideRowModelSteps } from "ag-grid-community";
import SidebarItem from "@/components/Custom/SidebarItem.vue";
import PopupAddFloorplan from "./components/PopupAddFloorplan.vue";

export default {
  components: {
    SidebarItem,
    HandleTableSidebar,
    PopupAddFloorplan,
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem
  },
  props: {
    venueId: {
      type: String,
      default: undefined,
      required: false
    }
  },
  data() {
    return {
      floorplans: [],
      activeFloorplan: null,
      activeVenueId: null,

      isLoading: true,

      move: false,

      mouseXY: { x: null, y: null },
      DragPos: { x: null, y: null, w: 1, h: 1, i: null },

      colNum: 64
    };
  },
  watch: {
    venueId: {
      handler: async function () {
        this.activeVenueId = this.venueId;
        // If no venue id specified, default to active venue
        if (typeof this.venueId == "undefined") {
          this.activeVenueId = this.$store.getters["venues/activeVenueId"];
        }

        this.refetchFloorplans();
      },
      immediate: true
    }
  },
  methods: {
    async refetchFloorplans() {
      this.isLoading = true;
      this.activeFloorplan = null;
      var floorplans = await this.$store.dispatch("pos/floorplan/getFloorplans", this.activeVenueId);
      console.log("FLOORPLANS", floorplans);

      this.floorplans = floorplans.map(floorplan => {
        return {
          ...floorplan,
          tables: floorplan.tables.map(e => {
            return {
              ...e,
              i: e.id,
              x: e.location.x,
              y: e.location.y,
              w: e.location.width,
              h: e.location.height
            };
          })
        };
      });

      if (this.floorplans.length > 0) {
        this.activeFloorplan = this.floorplans[0];
      } else {
        this.activeFloorplan = null;
      }

      this.isLoading = false;
    },

    onClickSave() {
      console.log("SAVE", this.floorplans);

      try {
        for (const floorplan of this.floorplans) {
          console.log("FLOORPLAN", floorplan);
          const mappedTables = floorplan.tables.map(e => {
            return {
              ...e,
              id: e.i,
              tableId: e.id,
              name: e.name,
              location: {
                x: e.x,
                y: e.y,
                width: e.w,
                height: e.h
              }
            };
          });

          console.log("Updating floor plan", mappedTables);

          this.$store.dispatch("pos/floorplan/updateFloorplan", {
            id: floorplan.id,
            dto: { name: floorplan.name, tables: mappedTables }
          });
        }

        this.$vs.notify({
          title: "Success",
          text: `Successfully updated venue floorplans`
        });
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: `Failed to update venue floorplans: ${error}`
        });
      }
    },

    confirmDelete() {
      if (!this.activeFloorplan) return;

      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Confirm deletion",
        text: `Are you sure you want to delete floorplan ${this.activeFloorplan.name}?`,
        accept: async () => {
          await this.$store.dispatch("pos/floorplan/deleteFloorplan", { id: this.activeFloorplan.id });
          this.$vs.notify({
            title: "Success",
            text: `Successfully delete venue floorplan ${this.activeFloorplan.name}`
          });

          await this.refetchFloorplans();
        }
      });
    },

    onClickTable(table) {
      // Only count as click if not dragging
      if (!this.move) {
        console.log("CLICKED", table);
        this.$refs.sidebar.show(table);
      }
    },

    onClickRemoveTable(table) {
      console.log("REMOVE", table);
      this.activeFloorplan.tables = this.activeFloorplan.tables.filter(e => e.id != table.id);
    },

    onMoved() {
      // Set to false on next render
      setTimeout(() => {
        this.move = false;
      });
    },

    drag(e) {
      let parentRect = document.getElementById("floorplanWrapper").getBoundingClientRect();
      let mouseInGrid = false;
      if (
        this.mouseXY.x > parentRect.left &&
        this.mouseXY.x < parentRect.right &&
        this.mouseXY.y > parentRect.top &&
        this.mouseXY.y < parentRect.bottom
      ) {
        mouseInGrid = true;
      }
      if (mouseInGrid === true && this.activeFloorplan.tables.findIndex(item => item.i === "drop") === -1) {
        this.activeFloorplan.tables.push({
          x: 0,
          y: 0,
          w: 6,
          h: 6,
          i: "drop"
        });
      }
      let index = this.activeFloorplan.tables.findIndex(item => item.i === "drop");
      if (index !== -1) {
        try {
          this.$refs.gridlayout.$children[this.activeFloorplan.tables.length].$refs.item.style.display = "none";
        } catch {
          console.log("error");
        }
        let el = this.$refs.gridlayout.$children[index];
        el.dragging = { top: this.mouseXY.y - parentRect.top, left: this.mouseXY.x - parentRect.left };
        let new_pos = el.calcXY(this.mouseXY.y - parentRect.top, this.mouseXY.x - parentRect.left);

        if (mouseInGrid === true) {
          this.$refs.gridlayout.dragEvent("dragstart", "drop", new_pos.x - 3, new_pos.y - 3, 6, 6);
          this.DragPos.i = String(index);
          this.DragPos.x = this.activeFloorplan.tables[index].x;
          this.DragPos.y = this.activeFloorplan.tables[index].y;
        }
        if (mouseInGrid === false) {
          this.$refs.gridlayout.dragEvent("dragend", "drop", new_pos.x - 3, new_pos.y - 3, 6, 6);
          this.activeFloorplan.tables = this.activeFloorplan.tables.filter(obj => obj.i !== "drop");
        }
      }
    },

    dragend(isNonTable, isRound, isRotated) {
      let parentRect = document.getElementById("floorplanWrapper").getBoundingClientRect();
      let mouseInGrid = false;
      if (
        this.mouseXY.x > parentRect.left &&
        this.mouseXY.x < parentRect.right &&
        this.mouseXY.y > parentRect.top &&
        this.mouseXY.y < parentRect.bottom
      ) {
        mouseInGrid = true;
      }
      if (mouseInGrid === true) {
        this.$refs.gridlayout.dragEvent("dragend", "drop", this.DragPos.x, this.DragPos.y, 6, 6);
        this.activeFloorplan.tables = this.activeFloorplan.tables.filter(obj => obj.i !== "drop");

        console.log("DRAGEND", this.DragPos);

        const id = new Date().getTime() + "";
        this.activeFloorplan.tables.push({
          id: id,
          tableId: id,
          guestCapacity: 4,
          name: "",
          isNonTable: isNonTable,
          isRound: isRound,
          isRotated: isRotated,
          location: {
            x: this.DragPos.x,
            y: this.DragPos.y,
            width: 6,
            height: 6
          },

          i: id,
          x: this.DragPos.x,
          y: this.DragPos.y,
          w: 6,
          h: 6
        });
      }
    }
  },
  computed: {
    ...mapGetters({
      activeVenue: "venues/activeVenue"
    })
  },
  mounted() {
    document.addEventListener(
      "dragover",
      e => {
        this.mouseXY.x = e.clientX;
        this.mouseXY.y = e.clientY;
      },
      false
    );
  },
  beforeDestroy() {
    document.removeEventListener("dragover", e => {
      this.mouseXY.x = e.clientX;
      this.mouseXY.y = e.clientY;
    });
  }
};
</script>

<style lang="scss" scoped>
.tables-container-wrapper {
  width: 832px; // 64 columns = 13px per column (incl 4 px margin)
  // background-color: #0000001e;
  border-radius: 12px;
  opacity: 1;

  background-image: radial-gradient(rgb(128, 128, 128) 12%, transparent 12%);
  background-position: 13px 13px;
  background-size: 13px 13px;
  background-color: rgba(148, 153, 148);
}

.vue-grid-item {
  background-color: #fff;
  border-radius: 5px;
  // padding: 0.5rem;

  &.vue-grid-placeholder {
    background: green;
  }
}

.static-element {
  color: white !important;
  background-color: #444 !important;
}

.round-table {
  border-radius: 50% !important;
}

.striped-bg {
  background: repeating-linear-gradient(
    -55deg,
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0) 10px,
    rgba(0, 0, 0, 0.12) 10px,
    rgba(0, 0, 0, 0.12) 20px
  );
}
</style>